import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getTasks, getReimbursements } from '../../api';
import { formatDateDdMmmYy } from '../../utils/format';

/**
 * The phone home screen.
 *
 * Deliberately not a shrunken Dashboard. The desktop Dashboard is a reporting
 * surface — charts, utilisation, WIP ageing — and none of that is what someone
 * opens their phone for. This is a launcher: what's due, what's waiting on me,
 * and one tap to the three things people do standing up.
 */

interface TaskRow {
  id: number;
  taskId: string;
  taskName: string;
  dueDate?: string | null;
  client?: { clientName?: string } | null;
}

const QUICK_ACTIONS = [
  { to: '/timesheets', icon: '⏱️', label: 'Log hours' },
  { to: '/reimbursements', icon: '💸', label: 'New claim' },
  { to: '/tasks', icon: '📋', label: 'My tasks' },
  { to: '/invoices', icon: '🧾', label: 'Invoices' },
];

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

/** Whole days from today; negative means overdue. */
function daysAway(iso: string): number {
  const due = new Date(iso);
  const today = new Date();
  due.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / 86400000);
}

function dueLabel(iso?: string | null): { text: string; tone: string } {
  if (!iso) return { text: 'No due date', tone: 'text-gray-400' };
  const d = daysAway(iso);
  if (d < 0) return { text: `${Math.abs(d)}d overdue`, tone: 'text-red-600 font-semibold' };
  if (d === 0) return { text: 'Due today', tone: 'text-amber-600 font-semibold' };
  if (d === 1) return { text: 'Due tomorrow', tone: 'text-amber-600' };
  return { text: formatDateDdMmmYy(iso), tone: 'text-gray-500' };
}

const MobileHome: React.FC = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [overdueCount, setOverdueCount] = useState(0);
  const [openClaims, setOpenClaims] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    // The task list is already scoped to what this user may see, so "my
    // tasks" needs no extra filter — the server has done it.
    const soon = getTasks({ status: 'OPEN', sortField: 'dueDate', sortDir: 'asc', page: 1, pageSize: 5 })
      .then((r) => (cancelled ? null : setTasks(r.data.rows ?? [])))
      .catch(() => {});

    const overdue = getTasks({ overdue: '1', page: 1, pageSize: 1 })
      .then((r) => (cancelled ? null : setOverdueCount(r.data.total ?? 0)))
      .catch(() => {});

    const claims = getReimbursements()
      .then((r) => {
        if (cancelled) return;
        const rows = Array.isArray(r.data) ? r.data : r.data?.rows ?? [];
        // Anything not yet settled or turned down is still "in flight" as far
        // as the person who filed it is concerned.
        setOpenClaims(
          rows.filter((c: any) => ['PENDING', 'REVIEWED', 'RETURNED', 'APPROVED'].includes(c.status)).length,
        );
      })
      .catch(() => {});

    Promise.all([soon, overdue, claims]).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const firstName = (user?.staffName || user?.email || '').split(/[\s@]/)[0];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900">
          {greeting()}
          {firstName ? `, ${firstName}` : ''}
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">
          {overdueCount > 0
            ? `${overdueCount} task${overdueCount === 1 ? '' : 's'} overdue`
            : loading
              ? 'Loading your day…'
              : 'Nothing overdue. Good place to be.'}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {QUICK_ACTIONS.map((a) => (
          <Link
            key={a.to}
            to={a.to}
            className="flex flex-col items-start gap-2 p-4 bg-white border border-gray-200 rounded-xl active:bg-gray-50 transition-colors"
          >
            <span className="text-2xl leading-none" aria-hidden="true">
              {a.icon}
            </span>
            <span className="text-sm font-medium text-gray-800">{a.label}</span>
          </Link>
        ))}
      </div>

      <section>
        <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Due next</h2>
        <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
          {loading ? (
            <p className="px-4 py-6 text-sm text-gray-400 text-center">Loading…</p>
          ) : tasks.length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-400 text-center">No open tasks</p>
          ) : (
            tasks.map((t) => {
              const due = dueLabel(t.dueDate);
              return (
                <Link key={t.id} to={`/tasks?taskId=${encodeURIComponent(t.taskId)}`} className="block px-4 py-3 active:bg-gray-50">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="text-sm font-medium text-gray-900 truncate">{t.taskName}</p>
                    <span className={`text-xs shrink-0 ${due.tone}`}>{due.text}</span>
                  </div>
                  <p className="text-xs text-gray-500 truncate mt-0.5">
                    {t.taskId}
                    {t.client?.clientName ? ` · ${t.client.clientName}` : ''}
                  </p>
                </Link>
              );
            })
          )}
        </div>
      </section>

      {openClaims > 0 && (
        <Link
          to="/reimbursements"
          className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-xl active:bg-gray-50"
        >
          <span className="text-2xl" aria-hidden="true">
            💸
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900">
              {openClaims} claim{openClaims === 1 ? '' : 's'} in progress
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Tap to see where they've got to</p>
          </div>
          <span className="text-gray-300">›</span>
        </Link>
      )}
    </div>
  );
};

export default MobileHome;
